import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTwitter,
  FaLinkedin,
  FaGlobe,
  FaPlus,
  FaTimes,
  FaSave,
  FaRedo,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaEdit,
  FaTiktok,
  FaPinterest,
  FaTelegram,
  FaSnapchat,
  FaReddit
} from 'react-icons/fa';

import { database } from '../firebase';
import { ref as dbRef, set, onValue } from 'firebase/database';

const AdminContact = () => {
  const [contactData, setContactData] = useState({
    location: '',
    emergencyContact: '',
    phoneNumber: '',
    emailSupport: '',
    generalEmail: '',
    whatsappNumber: '',
    workingHours: '',
    mapEmbedUrl: '',
    socialLinks: []
  });

  const [newSocial, setNewSocial] = useState({ platform: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch contact data from Firebase
  useEffect(() => {
    const contactRef = dbRef(database, 'Dr/contact');
    const unsubscribe = onValue(contactRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setContactData({
          location: data.location || '',
          emergencyContact: data.emergencyContact || '',
          phoneNumber: data.phoneNumber || '',
          emailSupport: data.emailSupport || '',
          generalEmail: data.generalEmail || '',
          whatsappNumber: data.whatsappNumber || '',
          workingHours: data.workingHours || '',
          mapEmbedUrl: data.mapEmbedUrl || '',
          socialLinks: data.socialLinks || []
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Generate Google Maps embed URL from address
  const generateMapEmbedUrl = (address) => {
    if (!address || address.trim() === '') return '';
    
    // Encode the address for URL
    const encodedAddress = encodeURIComponent(address);
    
    // Generate Google Maps embed URL using the address
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodedAddress}`;
  };

  // Get current map URL (use generated if mapEmbedUrl is empty)
  const getCurrentMapUrl = () => {
    if (contactData.mapEmbedUrl && contactData.mapEmbedUrl.trim() !== '') {
      return contactData.mapEmbedUrl;
    }
    return generateMapEmbedUrl(contactData.location);
  };

  // Handle input change
  const handleChange = (field, value) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

  // Add social link
  const addSocialLink = () => {
    if (newSocial.platform.trim() && newSocial.url.trim()) {
      setContactData(prev => ({
        ...prev,
        socialLinks: [...prev.socialLinks, { ...newSocial }]
      }));
      setNewSocial({ platform: '', url: '' });
    }
  };

  // Remove social link
  const removeSocialLink = (index) => {
    setContactData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index)
    }));
  };

  // Update to Firebase
  const handleUpdate = async () => {
    setSaving(true);
    try {
      // If mapEmbedUrl is empty, generate it from location
      const dataToSave = {
        ...contactData,
        mapEmbedUrl: contactData.mapEmbedUrl || generateMapEmbedUrl(contactData.location)
      };
      
      await set(dbRef(database, 'Dr/contact'), dataToSave);
      setSuccess('Contact information updated successfully!');
      setIsEditMode(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Failed to update. Please check console for details.');
    } finally {
      setSaving(false);
    }
  };

  // Reset to default (optional)
  const handleReset = () => {
    if (window.confirm('Reset all fields to default?')) {
      const defaultData = {
        location: '123 Medical Plaza, Health Street, Pune, Maharashtra - 411001',
        emergencyContact: '+91 98765 43210',
        phoneNumber: '+91 22 1234 5678',
        emailSupport: 'support@Anusaya.com',
        generalEmail: 'contact@Anusaya.com',
        whatsappNumber: '+91 98765 43210',
        workingHours: 'Mon - Sun: 8:00 AM - 10:00 PM\nEmergency: 24/7',
        mapEmbedUrl: '',
        socialLinks: [
          { platform: 'Instagram', url: 'https://instagram.com/Anusaya' },
          { platform: 'Facebook', url: 'https://facebook.com/Anusaya' },
          { platform: 'YouTube', url: 'https://youtube.com/Anusaya' },
          { platform: 'Twitter', url: 'https://twitter.com/Anusaya' },
          { platform: 'LinkedIn', url: 'https://linkedin.com/company/Anusaya' }
        ]
      };
      setContactData(defaultData);
    }
  };

  const getSocialIcon = (platform) => {
    const map = {
      Instagram: <FaInstagram className="text-pink-600" />,
      Facebook: <FaFacebook className="text-blue-600" />,
      YouTube: <FaYoutube className="text-red-600" />,
      Twitter: <FaTwitter className="text-sky-500" />,
      LinkedIn: <FaLinkedin className="text-blue-700" />,
      WhatsApp: <FaWhatsapp className="text-green-500" />,
      TikTok: <FaTiktok className="text-gray-900" />,
      Pinterest: <FaPinterest className="text-red-600" />,
      Telegram: <FaTelegram className="text-blue-500" />,
      Snapchat: <FaSnapchat className="text-yellow-400" />,
      Reddit: <FaReddit className="text-orange-600" />
    };
    return map[platform] || <FaGlobe className="text-gray-600" />;
  };

  // Open location in Google Maps
  const openInGoogleMaps = () => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactData.location)}`;
    window.open(mapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-pulse">
          <div className="text-xl text-gray-600">Loading contact details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* <nav className="text-sm text-gray-600 mb-4">
            <Link to="/admin/dashboard" className="hover:text-teal-600 transition-colors">Dashboard</Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-gray-800">Contact Settings</span>
          </nav> */}
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Contact Information</h1>
              <p className="text-gray-600 mt-2">Manage all contact details, social links & location</p>
            </div>
            
            {/* Edit Button */}
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg ${
                isEditMode 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
              }`}
            >
              <FaEdit />
              {isEditMode ? 'Cancel Edit' : 'Edit Information'}
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-2xl flex items-center animate-fade-in">
            <FaCheckCircle className="text-green-600 text-2xl mr-3" />
            <p className="text-green-800 font-semibold">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Details Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                <FaPhoneAlt className="mr-3 text-teal-600" />
                Contact Details
              </h2>

              <div className="space-y-6">
                {/* Location Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-red-500" />
                    Location Address
                  </label>
                  <div className="relative">
                    <textarea
                      value={contactData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      rows="3"
                      disabled={!isEditMode}
                      className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                        !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      placeholder="Full clinic/hospital address"
                    />
                    {contactData.location && (
                      <button
                        onClick={openInGoogleMaps}
                        className="absolute top-3 right-3 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Open in Google Maps"
                      >
                        <FaExternalLinkAlt />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Map will be automatically generated from this address
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaPhoneAlt className="mr-2 text-red-500" />
                      Emergency Contact
                    </label>
                    <input
                      type="text"
                      value={contactData.emergencyContact}
                      onChange={(e) => handleChange('emergencyContact', e.target.value)}
                      disabled={!isEditMode}
                      className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all ${
                        !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaPhoneAlt className="mr-2 text-teal-500" />
                      General Phone
                    </label>
                    <input
                      type="text"
                      value={contactData.phoneNumber}
                      onChange={(e) => handleChange('phoneNumber', e.target.value)}
                      disabled={!isEditMode}
                      className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 transition-all ${
                        !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      placeholder="+91 22 1234 5678"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaEnvelope className="mr-2 text-blue-500" />
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={contactData.emailSupport}
                      onChange={(e) => handleChange('emailSupport', e.target.value)}
                      disabled={!isEditMode}
                      className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                        !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      placeholder="support@yourclinic.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaEnvelope className="mr-2 text-purple-500" />
                      General Email
                    </label>
                    <input
                      type="email"
                      value={contactData.generalEmail}
                      onChange={(e) => handleChange('generalEmail', e.target.value)}
                      disabled={!isEditMode}
                      className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 transition-all ${
                        !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      placeholder="contact@yourclinic.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaWhatsapp className="mr-2 text-green-500" />
                    WhatsApp Number
                  </label>
                  <input
                    type="text"
                    value={contactData.whatsappNumber}
                    onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                    disabled={!isEditMode}
                    className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 transition-all ${
                      !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaClock className="mr-2 text-amber-500" />
                    Working Hours
                  </label>
                  <textarea
                    value={contactData.workingHours}
                    onChange={(e) => handleChange('workingHours', e.target.value)}
                    rows="4"
                    disabled={!isEditMode}
                    className={`w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 transition-all ${
                      !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                    placeholder="Mon - Fri: 9:00 AM - 8:00 PM&#10;Saturday: 10:00 AM - 6:00 PM&#10;Sunday: Emergency Only"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <FaMapMarkerAlt className="mr-2 text-teal-500" />
                      Google Maps Embed URL (Optional)
                    </span>
                    <a 
                      href="https://www.google.com/maps" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 text-xs hover:underline"
                    >
                      Get Custom Embed Code
                    </a>
                  </label>
                  <input
                    type="text"
                    value={contactData.mapEmbedUrl}
                    onChange={(e) => handleChange('mapEmbedUrl', e.target.value)}
                    disabled={!isEditMode}
                    className={`w-full p-4 border border-gray-300 rounded-xl text-sm font-mono focus:ring-2 focus:ring-teal-500 transition-all ${
                      !isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                    placeholder="Leave empty to auto-generate from address"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If empty, map will be automatically generated from the location address above
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                <FaGlobe className="mr-3 text-purple-600" />
                Social Media Links
              </h2>

              <div className="space-y-4 mb-6">
                {contactData.socialLinks.map((link, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="text-2xl">{getSocialIcon(link.platform)}</div>
                    <input
                      type="text"
                      value={link.platform}
                      onChange={(e) => {
                        const updated = [...contactData.socialLinks];
                        updated[i].platform = e.target.value;
                        setContactData(prev => ({ ...prev, socialLinks: updated }));
                      }}
                      disabled={!isEditMode}
                      className={`w-32 px-4 py-2 border rounded-lg transition-all ${
                        !isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => {
                        const updated = [...contactData.socialLinks];
                        updated[i].url = e.target.value;
                        setContactData(prev => ({ ...prev, socialLinks: updated }));
                      }}
                      disabled={!isEditMode}
                      className={`flex-1 px-4 py-2 border rounded-lg transition-all ${
                        !isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      placeholder="https://..."
                    />
                    {isEditMode && (
                      <button
                        onClick={() => removeSocialLink(i)}
                        className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add New - Only visible in edit mode */}
              {isEditMode && (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                  <select
                    value={newSocial.platform}
                    onChange={(e) => setNewSocial(prev => ({ ...prev, platform: e.target.value }))}
                    className="w-48 px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Select Platform</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Facebook">Facebook</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Twitter">Twitter</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Pinterest">Pinterest</option>
                    <option value="Telegram">Telegram</option>
                    <option value="Snapchat">Snapchat</option>
                    <option value="Reddit">Reddit</option>
                  </select>
                  <input
                    type="url"
                    value={newSocial.url}
                    onChange={(e) => setNewSocial(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://link.com"
                    className="flex-1 px-4 py-3 border rounded-lg"
                  />
                  <button
                    onClick={addSocialLink}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 transition-colors"
                  >
                    <FaPlus /> Add
                  </button>
                </div>
              )}
            </div>

            {/* Action Buttons - Only visible in edit mode */}
            {isEditMode && (
              <div className="flex gap-4 justify-end">
                <button
                  onClick={handleReset}
                  className="px-8 py-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium transition-colors"
                >
                  <FaRedo /> Reset to Default
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="px-10 py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:shadow-xl flex items-center gap-3 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaSave /> Update Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Preview Sidebar */}
          <div className="space-y-6">
            {/* Live Preview Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Live Preview</h3>
              
              <div className="space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-red-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">Location</p>
                    <p className="text-gray-600 whitespace-pre-line">{contactData.location || 'Not set'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <FaPhoneAlt className="text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">Emergency</p>
                    <p className="text-gray-700">{contactData.emergencyContact || 'Not set'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <FaWhatsapp className="text-green-600 text-xl flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">WhatsApp</p>
                    <p className="text-gray-700">{contactData.whatsappNumber || 'Not set'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">Email</p>
                    <p className="text-gray-700 break-all">{contactData.emailSupport || 'Not set'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaClock className="text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">Hours</p>
                    <p className="text-gray-600 whitespace-pre-line text-xs">{contactData.workingHours || 'Not set'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <p className="font-medium mb-3 text-gray-800">Social Links</p>
                <div className="flex flex-wrap gap-3">
                  {contactData.socialLinks.length > 0 ? (
                    contactData.socialLinks.map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="text-2xl hover:scale-110 transition-transform"
                        title={link.platform}>
                        {getSocialIcon(link.platform)}
                      </a>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No links added</p>
                  )}
                </div>
              </div>
            </div>

            {/* Map Preview */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-b flex items-center justify-between">
                <h3 className="font-bold text-gray-800 flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-teal-600" />
                  Map Preview
                </h3>
                {contactData.location && (
                  <button
                    onClick={openInGoogleMaps}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <FaExternalLinkAlt /> Open
                  </button>
                )}
              </div>
              {getCurrentMapUrl() ? (
                <iframe
                  src={getCurrentMapUrl()}
                  className="w-full h-64 border-0"
                  allowFullScreen=""
                  loading="lazy"
                  title="Location Map"
                ></iframe>
              ) : (
                <div className="h-64 bg-gray-200 flex flex-col items-center justify-center text-gray-500 p-6 text-center">
                  <FaMapMarkerAlt className="text-4xl mb-3 text-gray-400" />
                  <p className="font-medium">No location address provided</p>
                  <p className="text-xs mt-1">Enter an address to see the map preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContact;